import type {
  CrmContactRecommendationHint,
  CrmContactRecommendationHintPriority,
  CrmContactRecommendationHintSet,
  CrmContactRecommendationHintTone,
} from "@ease/crm/contact-recommendation-hints";
import { cn } from "@/lib/utils";

interface CrmRecommendationHintsCardProps {
  sets: CrmContactRecommendationHintSet[];
  title?: string;
  description?: string;
  className?: string;
}

export function CrmRecommendationHintsCard({
  sets,
  title = "Recommendation hints",
  description = "Tenant-configured operator recommendations with suggested talking points or next actions.",
  className,
}: CrmRecommendationHintsCardProps) {
  if (sets.length === 0) return null;

  const hintCount = sets.reduce((total, set) => total + set.hints.length, 0);

  return (
    <section className={cn("rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4", className)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">{title}</h3>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{description}</p>
        </div>
        <span className="shrink-0 rounded-full border border-[var(--color-border)] px-2.5 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
          {hintCount}
        </span>
      </div>
      <div className="mt-4 space-y-4">
        {sets.map((set) => (
          <section key={set.key} className="border-t border-[var(--color-border)] pt-4 first:border-t-0 first:pt-0">
            <div className="flex flex-col gap-1">
              <h4 className="text-sm font-semibold text-[var(--color-foreground)]">{set.label}</h4>
              {set.description ? (
                <p className="text-xs text-[var(--color-muted-foreground)]">{set.description}</p>
              ) : null}
            </div>
            <div className="mt-3 grid gap-2">
              {set.hints.map((hint) => (
                <RecommendationHintCard key={`${set.key}:${hint.key}`} hint={hint} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}

function RecommendationHintCard({ hint }: { hint: CrmContactRecommendationHint }) {
  return (
    <div className={cn(
      "rounded-lg border bg-[var(--color-background)] px-3 py-2",
      getCrmRecommendationHintBorderClasses(hint.tone),
    )}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-[var(--color-foreground)]">{hint.label}</p>
            <RecommendationPriorityChip priority={hint.priority} />
            {hint.category ? (
              <span className="rounded-full bg-[var(--color-muted)] px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                {hint.category.replaceAll("_", " ")}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-[var(--color-foreground)]">{hint.summary}</p>
          {hint.action ? (
            <p className="mt-1 text-xs font-medium text-[var(--color-primary)]">{hint.action}</p>
          ) : null}
        </div>
        <span className={cn(
          "shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium",
          getCrmRecommendationHintToneClasses(hint.tone),
        )}>
          {hint.tone}
        </span>
      </div>
      {hint.evidence ? (
        <p className="mt-2 rounded-md bg-[var(--color-surface)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
          {hint.evidence}
        </p>
      ) : null}
    </div>
  );
}

function RecommendationPriorityChip({ priority }: { priority: CrmContactRecommendationHintPriority }) {
  const label = priority === "critical" ? "Critical" : priority.charAt(0).toUpperCase() + priority.slice(1);
  return (
    <span className={cn(
      "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]",
      priority === "critical"
        ? "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]"
        : priority === "high"
          ? "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
          : priority === "low"
            ? "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]"
            : "bg-[var(--color-primary-light)] text-[var(--color-primary)]",
    )}>
      {label}
    </span>
  );
}

export function getCrmRecommendationHintToneClasses(tone: CrmContactRecommendationHintTone) {
  if (tone === "success") {
    return "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]";
  }
  if (tone === "warning") {
    return "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
  }
  if (tone === "danger") {
    return "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
  if (tone === "info") {
    return "border-[var(--color-info)]/30 bg-[var(--color-info)]/10 text-[var(--color-info)]";
  }
  return "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)]";
}

export function getCrmRecommendationHintBorderClasses(tone: CrmContactRecommendationHintTone) {
  if (tone === "success") return "border-[var(--color-success)]/25";
  if (tone === "warning") return "border-[var(--color-warning)]/25";
  if (tone === "danger") return "border-[var(--color-destructive)]/25";
  if (tone === "info") return "border-[var(--color-info)]/25";
  return "border-[var(--color-border)]";
}
